import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, ArrowRight } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import { canonicalUrl, SITE_URL } from "../data/seo";
import { credentialIcon } from "../data/credentialIcons";

const API = process.env.REACT_APP_BACKEND_URL;

export default function About() {
  const [about, setAbout] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/about`)
      .then(({ data }) => setAbout(data))
      .catch(() => setAbout(null));
  }, []);

  const tagsLine = (about?.tags || []).filter(Boolean).join(" · ");
  const bio = (about?.bio || []).filter(Boolean);

  usePageMeta({
    title: about?.seo?.title || "About the Author: Dr. Maydine L. Alcid-Digsby | Digsby Early Learning",
    description: about?.seo?.metaDescription || bio[0] || "",
    canonical: canonicalUrl("/about"),
    image: about?.headshot,
    type: "profile",
    jsonLd: about
      ? {
          "@context": "https://schema.org",
          "@type": "Person",
          name: about.name,
          jobTitle: tagsLine,
          description: bio[0] || "",
          image: about.headshot && about.headshot.startsWith("/") ? SITE_URL + about.headshot : about.headshot,
          url: `${SITE_URL}/about`,
          worksFor: { "@type": "Organization", name: "Digsby Early Learning", url: SITE_URL },
        }
      : undefined,
  });

  if (!about) {
    return (
      <div className="bg-[#FFFBF2] min-h-[60vh] flex items-center justify-center">
        <p className="text-slate-500" data-testid="about-loading">Loading…</p>
      </div>
    );
  }

  const credentials = (about.credentials || []).filter((c) => c && c.label);

  return (
    <div className="bg-[#FFFBF2]" data-testid="about-page">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid gap-12 lg:grid-cols-[400px_1fr] items-start">
        <div className="lg:sticky lg:top-28">
          <div className="relative">
            <div className="absolute inset-0 rotate-3 rounded-[2rem] bg-[#0000FF]/10" aria-hidden />
            {about.headshot && (
              <img
                src={about.headshot}
                alt={about.headshotAlt || about.name}
                className="relative rounded-[2rem] border-4 border-black shadow-[8px_8px_0px_#FBEF14] w-full object-cover aspect-[4/5]"
                data-testid="about-headshot"
              />
            )}
          </div>
        </div>

        <div>
          <span className="inline-flex items-center gap-2 text-sm font-bold text-[#FF0000] uppercase tracking-wider">
            <BookOpen size={16} /> About the Author
          </span>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl mt-3 text-black leading-tight" data-testid="about-name">
            {about.name}
          </h1>
          {tagsLine && (
            <p className="mt-2 font-heading font-bold text-lg text-[#0000FF]" data-testid="about-tags">
              {tagsLine}
            </p>
          )}

          <div className="mt-8 space-y-5 text-lg text-slate-700 leading-relaxed" data-testid="about-bio">
            {bio.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          {/* Pull quote */}
          {about.quote && (
            <blockquote className="mt-10 relative rounded-[2rem] bg-black text-white p-8 sm:p-10" data-testid="about-quote">
              <div className="absolute -top-5 left-8 text-6xl font-heading text-[#FBEF14] leading-none" aria-hidden>
                &ldquo;
              </div>
              <p className="font-heading font-bold text-xl sm:text-2xl leading-snug">{about.quote}</p>
              {about.quoteAttribution && (
                <footer className="mt-4 text-slate-300 font-medium">{about.quoteAttribution}</footer>
              )}
            </blockquote>
          )}
        </div>
      </section>

      {/* Credentials strip */}
      {credentials.length > 0 && (
        <section className="bg-white border-y-2 border-black/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-center">
              {about.credentialsHeading || "Credentials & Recognition"}
            </h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {credentials.map((c, i) => {
                const Icon = credentialIcon(c.icon);
                return (
                  <div
                    key={i}
                    data-testid={`credential-${i}`}
                    className="flex items-center gap-4 rounded-2xl bg-[#FFFBF2] border-2 border-black/10 p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
                  >
                    <span className="flex-shrink-0 w-12 h-12 rounded-full bg-[#0000FF] flex items-center justify-center">
                      <Icon size={22} className="text-white" />
                    </span>
                    <span className="font-heading font-bold text-slate-800 leading-tight">
                      {c.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="font-heading font-bold text-3xl sm:text-4xl">
          Explore the Books
        </h2>
        <p className="mt-4 text-lg text-slate-700">
          Discover the workbooks and story books Dr. Alcid-Digsby created to help every
          child learn, grow, and thrive.
        </p>
        <Link
          to="/shop"
          data-testid="about-shop-cta"
          className="mt-8 inline-flex items-center gap-2 min-h-[52px] rounded-full bg-[#FF0000] text-white font-bold text-lg px-8 hover:bg-[#d60000] hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
        >
          Shop the Books <ArrowRight size={20} />
        </Link>
      </section>
    </div>
  );
}
